import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, SearchIcon, XIcon } from 'lucide-react';
import { clsx } from 'clsx';

export type ComboboxOption = {
  value: string;
  label: string;
  /** Shown to the right of the label — a group, a hostname, a count. */
  sublabel?: string;
};

export type ComboboxProps = {
  /** Rendered above the control, bold, and wired to it as its name. */
  label: string;
  value: string | null;
  options: readonly ComboboxOption[];
  onChange: (value: string | null) => void;
  /** Shown in the trigger when nothing is selected. */
  placeholder?: string;
  /** Shown in the filter box inside the panel. */
  searchPlaceholder?: string;
  /** Shown in place of the list when the filter matches nothing. */
  emptyText?: string;
  disabled?: boolean;
  /** Monospace trigger and options, for hostnames and URLs. */
  mono?: boolean;
  className?: string;
};

/** How many options the panel renders at once. */
const MAX_VISIBLE = 200;

function matches(option: ComboboxOption, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return (
    option.label.toLowerCase().includes(needle) ||
    (option.sublabel ?? '').toLowerCase().includes(needle)
  );
}

/**
 * A select you can type into, and clear.
 *
 * Radix's Select cannot do either: it has no filter box, and no way to return
 * to "nothing selected" once a value is set. Both matter for a list of eight
 * hundred URLs, which is why this is a component rather than another Select.
 *
 * Built on plain elements rather than a popover library on purpose. The
 * behaviour needed here — a filter box, roving `aria-activedescendant`, Escape
 * to close, click-outside to dismiss — is the ⌘K palette's behaviour, already
 * proven in this codebase and costing nothing in jsdom, where every Radix
 * overlay costs seconds per mount.
 *
 * The panel is capped at `MAX_VISIBLE` options: a filter box is the way through
 * a long list, and rendering eight hundred rows to be scrolled past is slower
 * for the browser and no faster for the reader.
 */
export function Combobox({
  label,
  value,
  options,
  onChange,
  placeholder = 'Choose…',
  searchPlaceholder = 'Type to filter…',
  emptyText = 'Nothing matched',
  disabled = false,
  mono = false,
  className,
}: ComboboxProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const selected = options.find((option) => option.value === value) ?? null;
  const filtered = useMemo(
    () => options.filter((option) => matches(option, query)).slice(0, MAX_VISIBLE),
    [options, query],
  );

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(Math.max(0, filtered.findIndex((option) => option.value === value)));
    // Focus lands in the filter box, because typing is why the panel is open.
    input.current?.focus();
    // `filtered`/`value` are read once, when the panel opens; re-running this
    // on every keystroke would fight the reader's own cursor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (root.current !== null && !root.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  function choose(index: number) {
    const option = filtered[index];
    if (option === undefined) return;
    onChange(option.value);
    setOpen(false);
  }

  return (
    <div className={clsx('flex flex-col gap-(--space-1)', className)}>
      {/* Above the control and bold, so a row of controls reads as a row of
          named things rather than a sentence with a widget in it. */}
      <span id={`${id}-label`} className="text-[length:var(--text-small)] font-bold text-ink">
        {label}
      </span>

      <div ref={root} className="relative">
        <div className="flex items-stretch">
          <button
            type="button"
            id={id}
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-labelledby={`${id}-label`}
            {...(open ? { 'aria-controls': `${id}-list` } : {})}
            disabled={disabled}
            onClick={() => setOpen((current) => !current)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setOpen(true);
              }
            }}
            className={clsx(
              'flex h-(--control-height) min-w-0 flex-1 items-center justify-between gap-(--space-2)',
              'rounded-(--radius-md) border border-input bg-card px-(--space-3) text-left text-ink',
              'transition-[border-color,background-color] duration-(--duration-fast)',
              'hover:bg-surface-hover disabled:cursor-not-allowed disabled:bg-muted disabled:text-ink-subtle',
              selected === null && 'text-ink-subtle',
              /* The clear button tucks against the trigger's right edge, so
                 the two read as one control rather than a control and a
                 stray button. */
              selected !== null && 'rounded-r-none border-r-0',
            )}
          >
            <span className={clsx('truncate', mono && 'font-mono text-[length:var(--text-small)]')}>
              {selected?.label ?? placeholder}
            </span>
            <ChevronDownIcon aria-hidden="true" className="size-4 shrink-0 text-ink-subtle" />
          </button>

          {/* Only when there is something to clear: a permanently visible X
              that does nothing is furniture. */}
          {selected !== null && !disabled && (
            <button
              type="button"
              aria-label={`Clear ${label.toLowerCase()}`}
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              className="flex h-(--control-height) items-center rounded-r-(--radius-md) border border-input px-(--space-2) text-ink-subtle transition-colors duration-(--duration-fast) hover:bg-surface-hover hover:text-ink"
            >
              <XIcon aria-hidden="true" className="size-4" />
            </button>
          )}
        </div>

        {open && (
          <div className="absolute left-0 top-[calc(var(--control-height)+var(--space-1))] z-50 w-max min-w-full max-w-[32rem] overflow-hidden rounded-(--radius-md) border border-rule bg-popover shadow-(--shadow-overlay)">
            <div className="flex items-center gap-(--space-2) border-b border-rule px-(--space-3)">
              <SearchIcon aria-hidden="true" className="size-4 shrink-0 text-ink-subtle" />
              <input
                ref={input}
                type="text"
                role="searchbox"
                aria-label={`Filter ${label.toLowerCase()}`}
                aria-autocomplete="list"
                {...(filtered.length === 0 ? {} : { 'aria-controls': `${id}-list` })}
                aria-activedescendant={
                  filtered[active] === undefined ? undefined : `${id}-option-${active}`
                }
                placeholder={searchPlaceholder}
                spellCheck={false}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActive(0);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    setOpen(false);
                  } else if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    setActive((current) => Math.min(current + 1, filtered.length - 1));
                  } else if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    setActive((current) => Math.max(current - 1, 0));
                  } else if (event.key === 'Enter') {
                    event.preventDefault();
                    choose(active);
                  }
                }}
                className="h-(--control-height) w-full min-w-0 bg-transparent text-[length:var(--text-small)] text-ink outline-none placeholder:text-ink-subtle focus-visible:outline-none"
              />
            </div>

            {filtered.length === 0 ? (
              <p role="status" className="px-(--space-3) py-(--space-3) text-ink-subtle">
                {emptyText}
              </p>
            ) : (
              <ul
                id={`${id}-list`}
                role="listbox"
                aria-label={label}
                className="scroll-hairline max-h-[18rem]"
              >
                {filtered.map((option, index) => (
                  <li
                    key={option.value}
                    id={`${id}-option-${index}`}
                    role="option"
                    aria-selected={option.value === value}
                    onMouseEnter={() => setActive(index)}
                    onMouseDown={(event) => {
                      // Before blur, so the panel does not close under the
                      // pointer and swallow the click.
                      event.preventDefault();
                      choose(index);
                    }}
                    className={clsx(
                      'flex cursor-pointer items-baseline justify-between gap-(--space-4) px-(--space-3) py-(--space-2)',
                      index === active && 'bg-surface-hover',
                      option.value === value && 'text-accent',
                    )}
                  >
                    <span
                      className={clsx(
                        'truncate',
                        mono ? 'font-mono text-[length:var(--text-small)]' : '',
                      )}
                    >
                      {option.label}
                    </span>
                    {option.sublabel !== undefined && (
                      <span className="shrink-0 text-[length:var(--text-small)] text-ink-subtle">
                        {option.sublabel}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
